import React from 'react';

// MicroPython `network.WLAN(STA_IF).status()` codes (CYW43 on RP2350).
// Same shape `wifi_helper.py` returns as `status_code`. Humanise so the
// card is meaningful at a glance — `1 = link-down` looks like garbage
// otherwise.
const STATUS_TEXT = {
  0:  'idle',
  1:  'connecting (no link)',
  2:  'connecting',
  3:  'connected',
  '-1': 'connection failed',
  '-2': 'no AP found',
  '-3': 'auth failed',
};

function _statusLabel(code) {
  if (code === undefined || code === null) return 'unknown';
  const key = String(code);
  return STATUS_TEXT[key] !== undefined
    ? `${STATUS_TEXT[key]} (${code})`
    : `code ${code}`;
}

// Bucket RSSI into a 5-bar signal-strength glyph. -50 dBm and stronger
// is "great" (every bar); -90 dBm is "barely there". The thresholds
// match common Wi-Fi UX (matches macOS / Android bars).
function _rssiBars(rssi) {
  if (typeof rssi !== 'number') return null;
  let bars = 0;
  if (rssi >= -50) bars = 5;
  else if (rssi >= -60) bars = 4;
  else if (rssi >= -70) bars = 3;
  else if (rssi >= -80) bars = 2;
  else if (rssi >= -90) bars = 1;
  return bars;
}

function _Tile({ label, value, mono = true }) {
  return (
    <div style={{
      flex: '0 1 auto', minWidth: '120px',
      padding: '8px 12px', background: '#ffffff',
      borderRadius: '6px', border: '1px solid #e9ecef',
    }}>
      <div style={{
        fontSize: '0.7rem', color: '#6c757d',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>{label}</div>
      <div style={{
        fontSize: '1rem', fontWeight: 600, marginTop: '2px',
        fontFamily: mono ? '"IBM Plex Mono", monospace' : 'inherit',
        wordBreak: 'break-all',
      }}>{value || '—'}</div>
    </div>
  );
}

// Card for `kind: "pico.wifi_status"`. Renders Wi-Fi station-interface
// state as a row of tiles. The `note` field is shown when present (off-
// device run, network module unavailable, etc.) — keeps the surface
// honest about why some fields are empty.
export default function PicoWifiStatusCard({ structured }) {
  const {
    connected, status_code, ssid, bssid, rssi, channel,
    ip, netmask, gateway, dns, mac, ts, note,
  } = structured;

  const bars = _rssiBars(rssi);
  const barGlyph = bars !== null
    ? '▮'.repeat(bars) + '▯'.repeat(5 - bars)
    : null;

  return (
    <div style={{
      marginBottom: '10px', background: '#f8f9fa',
      borderRadius: '8px', border: '1px solid #e9ecef',
      padding: '12px 14px',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: '8px',
      }}>
        <div style={{
          fontSize: '0.85rem', fontWeight: 600,
          color: connected ? '#27ae60' : '#c0392b',
        }}>
          {connected ? '● connected' : '○ disconnected'}
          <span style={{
            color: '#6c757d', fontWeight: 400, marginLeft: '6px',
            fontSize: '0.8rem',
          }}>
            {_statusLabel(status_code)}
          </span>
        </div>
        {barGlyph && (
          <div style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.95rem', letterSpacing: '1px',
            color: '#495057',
          }} title={`RSSI ${rssi} dBm`}>
            {barGlyph}
            <span style={{
              marginLeft: '6px', fontSize: '0.75rem', color: '#6c757d',
            }}>{rssi} dBm</span>
          </div>
        )}
      </div>

      {note && (
        <div style={{
          fontSize: '0.85rem', color: '#856404',
          background: '#fff3cd', borderRadius: '4px',
          padding: '6px 10px', marginBottom: '8px',
        }}>
          {note}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <_Tile label="SSID" value={ssid} mono={false} />
        <_Tile label="BSSID" value={bssid} />
        <_Tile label="Channel" value={channel} />
        <_Tile label="IP" value={ip} />
        <_Tile label="Netmask" value={netmask} />
        <_Tile label="Gateway" value={gateway} />
        <_Tile label="DNS" value={dns} />
        <_Tile label="MAC" value={mac} />
      </div>

      {ts && (
        <div style={{
          marginTop: '10px', fontSize: '0.7rem', color: '#6c757d',
          fontFamily: '"IBM Plex Mono", monospace',
        }}>
          {ts}
        </div>
      )}
    </div>
  );
}
